import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, ShieldCheck, FileText, Mail, AlertCircle, EyeOff, Repeat, Target, X } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import api, { formatApiError } from "../lib/api";
import PayPalCheckout from "./PayPalCheckout";
import StripeCheckout from "./StripeCheckout";
import GoalProgress from "./GoalProgress";

const CURRENCY_OPTIONS = [
  { code: "EUR", symbol: "€" },
  { code: "USD", symbol: "$" },
  { code: "GBP", symbol: "£" },
];

const Donation = () => {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignSlug = searchParams.get("campaign") || null;
  const [campaign, setCampaign] = useState(null);
  const [tab, setTab] = useState("one_time");
  const [currency, setCurrency] = useState("EUR");
  const [selected, setSelected] = useState(50);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paypalConfigured, setPaypalConfigured] = useState(false);

  // Load campaign info if ?campaign=slug present
  useEffect(() => {
    if (!campaignSlug) {
      setCampaign(null);
      return;
    }
    api
      .get(`/campaigns/${campaignSlug}`)
      .then(({ data }) => {
        setCampaign(data);
        if (data?.currency) setCurrency(data.currency);
      })
      .catch(() => setCampaign(null));
  }, [campaignSlug]);

  const clearCampaign = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("campaign");
    setSearchParams(next);
    setCampaign(null);
  };

  const currencySymbol = useMemo(
    () => CURRENCY_OPTIONS.find((c) => c.code === currency)?.symbol || "€",
    [currency]
  );

  const amount = useMemo(() => {
    const c = parseFloat(custom);
    if (!isNaN(c) && c > 0) return c;
    return selected;
  }, [selected, custom]);

  const formValid = name.trim().length > 1 && /\S+@\S+\.\S+/.test(email) && amount > 0;

  // Check PayPal config from backend
  useEffect(() => {
    api
      .get("/paypal/config")
      .then(({ data }) => setPaypalConfigured(Boolean(data?.configured)))
      .catch(() => setPaypalConfigured(false));
  }, []);

  const goToThankYou = () => {
    navigate("/thank-you", {
      state: { name: anonymous ? "" : name, amount, currency, type: tab, anonymous, campaign_slug: campaignSlug },
    });
  };

  const handlePayPalSuccess = () => {
    goToThankYou();
  };

  const handleFallbackDonate = async () => {
    setSubmitting(true);
    try {
      await api.post("/donations", {
        donor_name: name,
        donor_email: email,
        amount,
        currency,
        donation_type: tab,
        paypal_order_id: null,
        paypal_payer_id: null,
        status_payment: "completed",
        message: message || null,
        anonymous,
        campaign_slug: campaignSlug,
      });
      toast.success(t.donate.success);
      goToThankYou();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.donate.error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscriptionStart = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/paypal/subscriptions", {
        amount,
        currency,
        donor_name: name,
        donor_email: email,
        message: message || null,
        anonymous,
        campaign_slug: campaignSlug,
      });
      if (data?.approval_url) {
        toast.success(t.donate.monthlyRedirect);
        // Stash donor info so /thank-you can show personalized message after redirect
        try {
          sessionStorage.setItem(
            "fcs_pending_subscription",
            JSON.stringify({
              subscription_id: data.subscription_id,
              name: anonymous ? "" : name,
              amount,
              currency,
              type: "monthly",
              anonymous,
            })
          );
        } catch (_) {}
        window.location.href = data.approval_url;
      } else {
        toast.error(t.donate.error);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.donate.error);
      setSubmitting(false);
    }
  };

  return (
    <section
      id="donate"
      className="py-24 md:py-32 bg-[#FDFBF7] relative"
      data-testid="donate-section"
    >
      <div className="container-narrow">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <span className="eyebrow" data-testid="donate-eyebrow">{t.donate.eyebrow}</span>
          <span className="gold-line mt-4 mb-6 mx-auto" />
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#0A192F] leading-[1.1] font-medium" data-testid="donate-title">
            {t.donate.title}
          </h2>
          <p className="text-[#334155] text-base md:text-lg leading-relaxed mt-5" data-testid="donate-subtitle">
            {t.donate.subtitle}
          </p>
        </div>

        <GoalProgress />

        {campaign && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl mx-auto mb-5 bg-[#FDF8EE] border border-[#C5A059]/30 rounded-xl p-4 flex items-start gap-3"
            data-testid="campaign-banner"
          >
            <div className="w-9 h-9 rounded-full bg-[#C5A059] flex items-center justify-center flex-shrink-0 mt-0.5">
              <Target size={16} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-[#C5A059] font-semibold">
                {lang === "fr" ? "Vous soutenez la campagne" : "You support the campaign"}
              </div>
              <div className="font-serif text-[#002B49] font-semibold text-lg mt-0.5 truncate" data-testid="campaign-banner-title">
                {lang === "en" ? campaign.title_en : campaign.title_fr}
              </div>
              <div className="text-xs text-[#64748B] mt-1">
                {Math.round(campaign.percentage)}% · {Math.round(campaign.raised_amount)} {campaign.currency} {lang === "fr" ? "sur" : "of"} {Math.round(campaign.goal_amount)} {campaign.currency}
              </div>
              <Link
                to={lang === "en" ? `/campaigns/${campaign.slug}` : `/campagnes/${campaign.slug}`}
                className="text-xs text-[#002B49] hover:text-[#C5A059] underline mt-1 inline-block"
              >
                {lang === "fr" ? "Voir la campagne" : "View campaign"}
              </Link>
            </div>
            <button
              onClick={clearCampaign}
              className="p-1.5 rounded-md text-[#64748B] hover:bg-black/5"
              aria-label="Remove campaign"
              data-testid="campaign-banner-close"
            >
              <X size={14} />
            </button>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,43,73,0.2)] border border-black/5 p-6 md:p-10"
          data-testid="donation-widget"
        >
          {/* Tabs + Currency */}
          <div className="flex flex-col md:flex-row gap-3 mb-8">
            <div className="grid grid-cols-2 bg-[#F3F0EA] rounded-lg p-1 flex-1" role="tablist">
              <button
                onClick={() => setTab("one_time")}
                className={`py-3 rounded-md font-medium text-sm transition-all ${
                  tab === "one_time" ? "bg-white text-[#002B49] shadow-sm" : "text-[#64748B] hover:text-[#002B49]"
                }`}
                data-testid="tab-one-time"
              >
                {t.donate.tabs.oneTime}
              </button>
              <button
                onClick={() => setTab("monthly")}
                className={`py-3 rounded-md font-medium text-sm transition-all ${
                  tab === "monthly" ? "bg-white text-[#002B49] shadow-sm" : "text-[#64748B] hover:text-[#002B49]"
                }`}
                data-testid="tab-monthly"
              >
                {t.donate.tabs.monthly}
              </button>
            </div>

            <div className="flex bg-[#F3F0EA] rounded-lg p-1" role="group" aria-label="Currency">
              {CURRENCY_OPTIONS.map((c) => (
                <button
                  key={c.code}
                  onClick={() => setCurrency(c.code)}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                    currency === c.code ? "bg-[#002B49] text-white" : "text-[#64748B] hover:text-[#002B49]"
                  }`}
                  data-testid={`currency-${c.code}`}
                >
                  {c.symbol} {c.code}
                </button>
              ))}
            </div>
          </div>

          {/* Amount selector */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {t.donate.amounts.map((a) => (
              <button
                key={a}
                onClick={() => {
                  setSelected(a);
                  setCustom("");
                }}
                className={`py-4 rounded-lg border font-semibold transition-all ${
                  selected === a && !custom
                    ? "bg-[#002B49] text-white border-[#002B49]"
                    : "bg-[#FDFBF7] text-[#002B49] border-[#E5E7EB] hover:border-[#002B49]"
                }`}
                data-testid={`donate-amount-${a}`}
              >
                {currencySymbol} {a}
                {tab === "monthly" && (
                  <span className="block text-[10px] font-normal opacity-70 mt-0.5">
                    / {lang === "fr" ? "mois" : "month"}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
              {t.donate.customLabel}
            </label>
            <div className="relative mt-2">
              <input
                type="number"
                min="1"
                step="1"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="—"
                className="w-full px-4 py-3 pr-10 rounded-lg border border-[#E5E7EB] focus:border-[#002B49] focus:ring-2 focus:ring-[#002B49]/10 outline-none text-[#0A192F] font-medium"
                data-testid="donate-amount-custom"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748B] font-semibold">
                {currencySymbol}
              </span>
            </div>
          </div>

          {/* Impact line */}
          {amount > 0 && (
            <div className="bg-[#FDF8EE] border border-[#C5A059]/30 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="impact-line">
              <div className="w-8 h-8 rounded-full bg-[#C5A059] flex items-center justify-center flex-shrink-0 mt-0.5">
                <ShieldCheck size={16} className="text-white" />
              </div>
              <p className="text-sm text-[#0A192F] leading-relaxed">
                {t.donate.impactLine(amount).replace("€", currencySymbol)}
              </p>
            </div>
          )}

          {/* Donor info */}
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                {t.donate.formName}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full mt-2 px-4 py-3 rounded-lg border border-[#E5E7EB] focus:border-[#002B49] focus:ring-2 focus:ring-[#002B49]/10 outline-none"
                data-testid="donor-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                {t.donate.formEmail}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-2 px-4 py-3 rounded-lg border border-[#E5E7EB] focus:border-[#002B49] focus:ring-2 focus:ring-[#002B49]/10 outline-none"
                data-testid="donor-email-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
              {t.donate.formMessage}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="2"
              className="w-full mt-2 px-4 py-3 rounded-lg border border-[#E5E7EB] focus:border-[#002B49] focus:ring-2 focus:ring-[#002B49]/10 outline-none resize-none"
              data-testid="donor-message-input"
            />
          </div>

          {tab === "monthly" && (
            <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-3 text-sm mb-5 flex items-start gap-2" data-testid="monthly-note">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <p>{t.donate.monthlyNote}</p>
            </div>
          )}

          {/* Anonymous toggle */}
          <label className="flex items-start gap-3 mb-5 cursor-pointer group" data-testid="anonymous-label">
            <input
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#002B49] cursor-pointer"
              data-testid="anonymous-checkbox"
            />
            <span className="flex items-start gap-2 text-sm text-[#334155] group-hover:text-[#002B49] transition-colors">
              <EyeOff size={14} className="mt-0.5 text-[#C5A059] flex-shrink-0" />
              {t.donate.anonymousLabel}
            </span>
          </label>

          {/* PayPal v6 button (one-time) or Subscription button (monthly) or fallback */}
          <div className="mt-2 min-h-[55px]" data-testid="paypal-zone">
            {!formValid ? (
              <button
                disabled
                className="w-full py-4 bg-[#C5A059]/40 text-white rounded-lg font-semibold cursor-not-allowed"
                data-testid="paypal-disabled-btn"
              >
                {lang === "fr"
                  ? "Renseignez nom, email et montant"
                  : "Please fill name, email and amount"}
              </button>
            ) : !paypalConfigured ? (
              <div className="space-y-3">
                <button
                  onClick={handleFallbackDonate}
                  disabled={submitting}
                  className="w-full btn-primary justify-center py-4 disabled:opacity-60"
                  data-testid="donate-fallback-btn"
                >
                  {submitting
                    ? t.donate.processing
                    : `${t.hero.ctaPrimary} — ${currencySymbol} ${amount}`}
                </button>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{t.donate.paypalNotConfigured}</span>
                </p>
              </div>
            ) : tab === "monthly" ? (
              <button
                onClick={handleSubscriptionStart}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 btn-primary py-4 disabled:opacity-60"
                data-testid="subscription-start-btn"
              >
                <Repeat size={18} />
                {submitting
                  ? t.donate.monthlyRedirect
                  : `${t.donate.monthlyButton} — ${currencySymbol} ${amount}/${lang === "fr" ? "mois" : "mo"}`}
              </button>
            ) : (
              <div className="space-y-3" data-testid="payment-options">
                <StripeCheckout
                  amount={amount}
                  currency={currency}
                  donorName={name}
                  donorEmail={email}
                  message={message}
                  anonymous={anonymous}
                  locale={lang}
                  campaignSlug={campaignSlug}
                  t={t.donate}
                />
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-[#64748B]" aria-hidden>
                  <span className="flex-1 h-px bg-[#E5E7EB]" />
                  {lang === "fr" ? "ou" : "or"}
                  <span className="flex-1 h-px bg-[#E5E7EB]" />
                </div>
                <PayPalCheckout
                  amount={amount}
                  currency={currency}
                  donorName={name}
                  donorEmail={email}
                  message={message}
                  donationType={tab}
                  locale={lang}
                  anonymous={anonymous}
                  campaignSlug={campaignSlug}
                  onSuccess={handlePayPalSuccess}
                />
              </div>
            )}
          </div>

          {/* Trust badges */}
          <div className="mt-8 pt-8 border-t border-[#E5E7EB]">
            <div className="text-xs uppercase tracking-wider text-[#64748B] mb-4 font-medium">
              {t.donate.trustTitle}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[Lock, ShieldCheck, FileText, Mail].map((Icon, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-xs text-[#334155]"
                  data-testid={`trust-item-${i}`}
                >
                  <Icon size={16} className="text-[#002B49] flex-shrink-0" />
                  <span>{t.donate.trustItems[i]}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#64748B] mt-5 leading-relaxed">{t.donate.legal}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Donation;
